import { Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Typography } from "@material-ui/core";
import Select from "@material-ui/core/Select";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import React from "react";
import { useIntl } from "react-intl";
import {
  ModuleCertificateValidity,
  ModuleCertificateValidityTranslationKey,
  ModuleGradingRules,
  ModuleGradingRulesTranslationKey,
  ModuleStatus,
  ModuleType,
  ModuleTypeTranslationKey,
  ModuleViewModel,
  RequiredModule,
} from "../../model/Module";
import { GetTranslation } from "../../translations/GetTranslation";
import GridCurrencyField from "../common/customGrid/GridCurrencyField";
import GridTextField from "../common/customGrid/GridTextField";
import TranslatedMessage from "../translations/TranslatedMessage";

type ModuleFormFieldsProps = {
  module: ModuleViewModel;
  handleFieldChange: (change: Partial<ModuleViewModel>) => void;
  requiredFieldStatus: RequiredModule;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingBottom: theme.spacing(6),
    },
    moduleDetails: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    documentUploadCheckbox: {
      [theme.breakpoints.down("xs")]: {
        order: -2,
      },
    },
    allowOptionsCheckbox: {
      [theme.breakpoints.down("xs")]: {
        order: -3,
      },
    },
    validationRequiredCheckbox: {
      [theme.breakpoints.down("xs")]: {
        order: -1,
      },
    },
  })
);

const ModuleFormFields = (props: ModuleFormFieldsProps) => {
  const { module, handleFieldChange, requiredFieldStatus } = props;
  const intl = useIntl();
  const classes = useStyles();

  return (
    <React.Fragment>
      <Grid container={true} direction="column" spacing={4}>
        <Grid item={true}>
          <Grid container={true} spacing={4}>
            <GridTextField
              error={requiredFieldStatus.isNameDEEmpty}
              required
              label={GetTranslation(intl, "module.field.moduleNameDE")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onChange={(event) => handleFieldChange({ nameDE: event.target.value })}
              value={module.nameDE || ""}
              readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
            />
            <GridTextField
              error={requiredFieldStatus.isNameENEmpty}
              required
              label={GetTranslation(intl, "module.field.moduleNameEN")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onChange={(event) => handleFieldChange({ nameEN: event.target.value })}
              value={module.nameEN || ""}
              readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
            />
            <GridTextField
              error={requiredFieldStatus.isNameFREmpty}
              required
              label={GetTranslation(intl, "module.field.moduleNameFR")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onChange={(event) => handleFieldChange({ nameFR: event.target.value })}
              value={module.nameFR || ""}
              readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
            />
            <GridTextField
              error={requiredFieldStatus.isNameITEmpty}
              required
              label={GetTranslation(intl, "module.field.moduleNameIT")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onChange={(event) => handleFieldChange({ nameIT: event.target.value })}
              value={module.nameIT || ""}
              readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
            />
          </Grid>
        </Grid>
        <Grid item={true} container={true} direction="column" spacing={1}>
          <Grid item={true}>
            <Typography variant="h6">
              <TranslatedMessage id={"module.moduleDetails"} />
            </Typography>
          </Grid>
          <Grid item={true} container spacing={4}>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} className={classes.allowOptionsCheckbox}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={module.allowOptions}
                    onChange={(event) => handleFieldChange({ allowOptions: event.target.checked })}
                    disabled={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                  />
                }
                label={<TranslatedMessage id="module.field.allowOptions" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <TranslatedMessage id="module.field.moduleType" />
                </InputLabel>
                <Select
                  value={module.moduleType}
                  onChange={(event) => handleFieldChange({ moduleType: event.target.value as ModuleType })}
                  error={false}
                  readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                >
                  <MenuItem value={ModuleType.Mandatory}>
                    <TranslatedMessage id={ModuleTypeTranslationKey[ModuleType.Mandatory]} />
                  </MenuItem>
                  <MenuItem value={ModuleType.OptionalModule}>
                    <TranslatedMessage id={ModuleTypeTranslationKey[ModuleType.OptionalModule]} />
                  </MenuItem>
                  <MenuItem value={ModuleType.ProcessModule}>
                    <TranslatedMessage id={ModuleTypeTranslationKey[ModuleType.ProcessModule]} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} className={classes.documentUploadCheckbox}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={module.allowDocumentUpload}
                    onChange={(event) => handleFieldChange({ allowDocumentUpload: event.target.checked })}
                    disabled={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                  />
                }
                label={<TranslatedMessage id="module.field.allowDocumentUpload" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <FormControl fullWidth>
                <InputLabel>
                  <TranslatedMessage id="module.field.certificateValidity" />
                </InputLabel>
                <Select
                  value={module.certificateValidity}
                  onChange={(event) =>
                    handleFieldChange({ certificateValidity: event.target.value as ModuleCertificateValidity })
                  }
                  error={false}
                  readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                >
                  <MenuItem value={ModuleCertificateValidity.Unlimited}>
                    <TranslatedMessage
                      id={ModuleCertificateValidityTranslationKey[ModuleCertificateValidity.Unlimited]}
                    />
                  </MenuItem>
                  <MenuItem value={ModuleCertificateValidity.FiveYears}>
                    <TranslatedMessage
                      id={ModuleCertificateValidityTranslationKey[ModuleCertificateValidity.FiveYears]}
                    />
                  </MenuItem>
                  <MenuItem value={ModuleCertificateValidity.OneYear}>
                    <TranslatedMessage
                      id={ModuleCertificateValidityTranslationKey[ModuleCertificateValidity.OneYear]}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} className={classes.validationRequiredCheckbox}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={module.verificationRequired}
                    onChange={(event) => handleFieldChange({ verificationRequired: event.target.checked })}
                    disabled={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                  />
                }
                label={<TranslatedMessage id="module.field.verificationRequired" />}
              />
            </Grid>
            <GridCurrencyField
              error={requiredFieldStatus.isPriceEmpty}
              required
              label={GetTranslation(intl, "module.field.price")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onFieldChange={(event: any, value: number) => handleFieldChange({ price: value })}
              value={module.price}
              readOnly={module.status === ModuleStatus.Archived}
            />
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6} className={classes.validationRequiredCheckbox}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={module.allowRegistrationOnlyOnce}
                    onChange={(event) => handleFieldChange({ allowRegistrationOnlyOnce: event.target.checked })}
                    disabled={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                  />
                }
                label={<TranslatedMessage id="module.field.allowRegistrationOnlyOnce" />}
              />
            </Grid>
            <GridTextField
              label={GetTranslation(intl, "module.field.abacusNumber")}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
              onChange={(event) => handleFieldChange({ abacusNumber: event.target.value })}
              value={module.abacusNumber || ""}
              readOnly={module.status === ModuleStatus.Archived}
            />
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <FormControl required fullWidth error={requiredFieldStatus.isGradingRuleEmpty}>
                <InputLabel>
                  <TranslatedMessage id="module.field.gradingRules" />
                </InputLabel>
                <Select
                  value={module.gradingRule}
                  onChange={(event) => handleFieldChange({ gradingRule: event.target.value as ModuleGradingRules })}
                  readOnly={module.status === ModuleStatus.Published || module.status === ModuleStatus.Archived}
                >
                  <MenuItem value={ModuleGradingRules.AverageGrade}>
                    <TranslatedMessage id={ModuleGradingRulesTranslationKey[ModuleGradingRules.AverageGrade]} />
                  </MenuItem>
                  <MenuItem value={ModuleGradingRules.AllExams}>
                    <TranslatedMessage id={ModuleGradingRulesTranslationKey[ModuleGradingRules.AllExams]} />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ModuleFormFields;
